export interface NexApHeaderTaxFields {
    tax: number;
    gstHSTTax: number;
    pstTax: number;
}

const toTwoDecimals = (value: number): number => Math.round((value || 0) * 100) / 100;

/** Minimum |tax| to emit a Sales Tax AP dist line (matches money rounding). */
const SALES_TAX_LINE_EPSILON = 0.005;

/**
 * Whether US sales tax should be emitted as an AP distribution line (not header tax).
 * Canada / `split` GST+PST always stays on the header regardless of the org setting.
 */
export function shouldEmitSalesTaxAsApDistLine(args: {
    salesTaxAsLineItem?: boolean;
    nexTaxFormat?: 'single' | 'split';
    taxesTotal: number;
}): boolean {
    if (!args.salesTaxAsLineItem) return false;
    if (args.nexTaxFormat === 'split') return false;
    return Math.abs(args.taxesTotal || 0) >= SALES_TAX_LINE_EPSILON;
}

/**
 * Resolve NEX `apHeader` tax fields based on invoice tax format.
 *
 * - **single** (default / US / generic Canadian "Sales Tax"):
 *   → `tax` = total tax, `gstHSTTax` = 0, `pstTax` = 0
 *
 * - **split** (Canadian invoices showing GST/HST + PST separately):
 *   → `tax` = 0, `gstHSTTax` = GST/HST amount, `pstTax` = PST amount
 *
 * - **salesTaxAsLineItem** (US only; ignored for split):
 *   → all header tax fields `0` (tax is an AP dist line instead when non-zero)
 */
export function resolveNexApHeaderTaxFields(args: {
    nexTaxFormat: 'single' | 'split' | undefined;
    taxesTotal: number;
    pstTaxTotal?: number;
    /** When true and format is not split, header tax is zeroed (tax goes on an AP dist line). */
    salesTaxAsLineItem?: boolean;
}): NexApHeaderTaxFields {
    // US sales-tax-as-line-item: never put tax on the header (dual-send fails on NEX).
    if (args.salesTaxAsLineItem && args.nexTaxFormat !== 'split') {
        return { tax: 0, gstHSTTax: 0, pstTax: 0 };
    }
    if (args.nexTaxFormat === 'split') {
        return {
            tax: 0,
            gstHSTTax: toTwoDecimals(args.taxesTotal),
            pstTax: toTwoDecimals(args.pstTaxTotal ?? 0),
        };
    }
    return {
        tax: toTwoDecimals(args.taxesTotal),
        gstHSTTax: 0,
        pstTax: 0,
    };
}
